import { LocalizedElement } from '../i18n/Language';
import WhatsAppIcon from './WhatsAppIcon';
import { PolicyLinks } from './BookingPolicies';
import React, { useState, useEffect } from 'react';
import { Link } from '../i18n/links';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Youtube, Star } from 'lucide-react';
import { businessInfo } from '../mockData';
import { trackEvent } from '../lib/analytics';
import site from '../content/siteContent.json';
import { getInitialContent } from '../api';

const footerLocations = [
{ city: 'Satna', slug: 'satna' },
{ city: 'Rewa', slug: 'rewa' },
{ city: 'Khajuraho', slug: 'khajuraho' },
{ city: 'Maihar', slug: 'maihar' },
{ city: 'Chitrakoot', slug: 'chitrakoot' },
{ city: 'Jabalpur', slug: 'jabalpur' }];


const Footer = () => {
  // A saved build may be served in a later year. Hydrate its original text first.
  const [currentYear, setCurrentYear] = useState(() =>
  getInitialContent('/buildYear') ?? new Date().getFullYear()
  );

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  return (
    <LocalizedElement as="footer" className="bg-gray-900 text-gray-300">
      <LocalizedElement as="div" className="container mx-auto px-4 py-12">
        {/* Google Reviews CTA */}
        <LocalizedElement as="div" className="bg-blue-600 text-white rounded-lg p-6 mb-8 text-center">
          <LocalizedElement as="div" className="flex items-center justify-center gap-2 mb-3">
            <Star className="fill-yellow-400 text-yellow-400" size={24} />
            <Star className="fill-yellow-400 text-yellow-400" size={24} />
            <Star className="fill-yellow-400 text-yellow-400" size={24} />
            <Star className="fill-yellow-400 text-yellow-400" size={24} />
            <Star className="fill-yellow-400 text-yellow-400" size={24} />
          </LocalizedElement>
          <LocalizedElement as="h3" className="text-xl font-bold mb-2">Rate Us on Google!</LocalizedElement>
          <LocalizedElement as="p" className="mb-4">Share your experience with VVPSS Taxi Services</LocalizedElement>
          <LocalizedElement as="a"
          href="https://share.google/OqglooUbojgKZZHMB"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">

            Write a Google Review
          </LocalizedElement>
        </LocalizedElement>

        <LocalizedElement as="div" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <LocalizedElement as="div">
            <LocalizedElement as="img"
            src="/brand/logo-56.webp?v=badge-e75c0e4c89f4"
            srcSet="/brand/logo-56.webp?v=badge-e75c0e4c89f4 1x, /brand/logo-112.webp?v=badge-e75c0e4c89f4 2x, /brand/logo-168.webp?v=badge-e75c0e4c89f4 3x"
            alt="VVPSS Taxi Services logo"
            width="44"
            height="44"
            loading="lazy"
            decoding="async"
            className="h-11 w-11 shrink-0 rounded-lg bg-white/90 object-contain p-1 mb-4" />

            <LocalizedElement as="p" className="text-sm mb-4">
              {site.bookingServicesSummary}
            </LocalizedElement>
            <LocalizedElement as="div" className="flex gap-3">
              <LocalizedElement as="a" href="#" aria-label="VVPSS on Facebook" className="bg-blue-600 p-2.5 rounded hover:bg-blue-700 transition-colors">
                <Facebook size={18} aria-hidden="true" />
              </LocalizedElement>
              <LocalizedElement as="a" href="#" aria-label="VVPSS on Twitter" className="bg-blue-400 p-2.5 rounded hover:bg-blue-500 transition-colors">
                <Twitter size={18} aria-hidden="true" />
              </LocalizedElement>
              <LocalizedElement as="a" href="#" aria-label="VVPSS on Instagram" className="bg-pink-600 p-2.5 rounded hover:bg-pink-700 transition-colors">
                <Instagram size={18} aria-hidden="true" />
              </LocalizedElement>
              <LocalizedElement as="a" href="#" aria-label="VVPSS on YouTube" className="bg-red-600 p-2.5 rounded hover:bg-red-700 transition-colors">
                <Youtube size={18} aria-hidden="true" />
              </LocalizedElement>
            </LocalizedElement>
            <LocalizedElement as="a"
            href={`https://wa.me/91${businessInfo.whatsapp}?text=${encodeURIComponent('Hello, I want to book a taxi with VVPSS. Please share the details.')}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('whatsapp_click', { location: 'footer', page_url: typeof window !== 'undefined' ? window.location.href : '' })}
            className="mt-5 inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
            data-testid="footer-whatsapp-cta">

              <WhatsAppIcon size={18} />
              Chat on WhatsApp
            </LocalizedElement>
          </LocalizedElement>

          {/* Quick Links */}
          <LocalizedElement as="div">
            <LocalizedElement as="h3" className="text-white font-semibold text-lg mb-4">Quick Links</LocalizedElement>
            <LocalizedElement as="ul" className="space-y-2">
              <LocalizedElement as="li">
                <LocalizedElement as={Link} to="/" className="hover:text-blue-400 transition-colors">Home</LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li">
                <LocalizedElement as={Link} to="/about" className="hover:text-blue-400 transition-colors">About Us</LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li">
                <LocalizedElement as={Link} to="/contact" className="hover:text-blue-400 transition-colors">Contact Us</LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li">
                <LocalizedElement as={Link} to="/#services" className="hover:text-blue-400 transition-colors">Our Services</LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li">
                <LocalizedElement as={Link} to="/#vehicles" className="hover:text-blue-400 transition-colors">Our Fleet</LocalizedElement>
              </LocalizedElement>
            </LocalizedElement>
          </LocalizedElement>

          {/* Service Locations */}
          <LocalizedElement as="div">
            <LocalizedElement as="h3" className="text-white font-semibold text-lg mb-4">Service Locations</LocalizedElement>
            <LocalizedElement as="ul" className="space-y-2">
              {footerLocations.map((location) =>
              <LocalizedElement as="li" key={location.slug}>
                  <LocalizedElement as={Link}
                to={`/taxi-service/${location.slug}`}
                className="hover:text-blue-400 transition-colors">

                    Taxi in {location.city}
                  </LocalizedElement>
                </LocalizedElement>
              )}
            </LocalizedElement>
          </LocalizedElement>

          {/* Contact Info */}
          <LocalizedElement as="div">
            <LocalizedElement as="h3" className="text-white font-semibold text-lg mb-4">Contact Us</LocalizedElement>
            <LocalizedElement as="ul" className="space-y-3">
              <LocalizedElement as="li" className="flex items-start gap-2">
                <MapPin size={18} className="text-blue-400 mt-1 flex-shrink-0" />
                <LocalizedElement as="span" className="text-sm">{businessInfo.address}</LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li" className="flex items-center gap-2">
                <Phone size={18} className="text-blue-400 flex-shrink-0" />
                <LocalizedElement as="div" className="text-sm space-y-1">
                  <LocalizedElement as="a" href={`tel:+91${businessInfo.phone1}`} className="hover:text-blue-400 transition-colors block py-1">
                    {businessInfo.phone1}
                  </LocalizedElement>
                  <LocalizedElement as="a" href={`tel:${businessInfo.phone2}`} className="hover:text-blue-400 transition-colors block py-1">
                    {businessInfo.phone2}
                  </LocalizedElement>
                  <LocalizedElement as="a" href={`tel:${businessInfo.phone3}`} className="hover:text-blue-400 transition-colors block py-1">
                    {businessInfo.phone3}
                  </LocalizedElement>
                </LocalizedElement>
              </LocalizedElement>
              <LocalizedElement as="li" className="flex items-center gap-2">
                <Mail size={18} className="text-blue-400 flex-shrink-0" />
                <LocalizedElement as="a"
                href={`mailto:${businessInfo.email}`}
                className="text-sm hover:text-blue-400 transition-colors break-all">

                  {businessInfo.email}
                </LocalizedElement>
              </LocalizedElement>
            </LocalizedElement>
          </LocalizedElement>
        </LocalizedElement>

        <PolicyLinks className="mt-8 text-gray-200" />
        {/* Bottom Bar */}
        <LocalizedElement as="div" className="border-t border-gray-800 mt-8 pt-6 text-center text-sm">
          <LocalizedElement as="p">
            © {currentYear} {businessInfo.name}. All rights reserved.
            <LocalizedElement as="span" className="hidden md:inline"> | Designed for Excellence in Taxi Services</LocalizedElement>
          </LocalizedElement>
          <div lang="en" className="mt-3 min-w-0 pb-8" data-testid="footer-developer-credit">
            <p className="text-gray-400 leading-relaxed">
              Website Designed &amp; Developed by{' '}
              <a
                href="https://mynavodayans.in/contact?service=custom-website-development"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Mynavodayans Online Services Pvt. Ltd. — website development enquiries (opens in a new tab)"
                className="rounded-sm text-blue-300 underline underline-offset-4 decoration-blue-300/50 hover:text-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-300"
              >
                Mynavodayans Online Services Pvt. Ltd.
              </a>
            </p>
            <div className="mt-6 flex min-w-0 flex-col items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800/40 p-4 sm:flex-row sm:text-left">
              <p className="min-w-0 text-sm font-medium text-gray-200">
                Need a Professional Website for Your Business?
              </p>
              <a
                href="https://mynavodayans.in/contact?service=custom-website-development"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get Your Website from Mynavodayans Online Services Pvt. Ltd. (opens in a new tab)"
                className="inline-flex min-h-[44px] max-w-full shrink-0 items-center justify-center rounded-lg border border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-100 transition-colors hover:border-gray-400 hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-300"
              >
                Get Your Website
              </a>
            </div>
          </div>
        </LocalizedElement>
      </LocalizedElement>
    </LocalizedElement>);

};

export default Footer;
